import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FetchNestedCategories, FetchProductsBySubCategory } from "../Data";
import FiltersSidebar from "./FiltersSidebar";
import MobileFiltersDrawer from "./MobileFiltersDrawer";
import ProductGrid from "./ProductGrid";

/**
 * Amazon-style Category Page
 * 2-column layout: Filters sidebar (left) + Product grid (right)
 * Mobile: Filters in drawer, products in single column
 */
const CategoryPage = () => {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  
  const [category, setCategory] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    selectedSubCategories: [],
    priceMin: null,
    priceMax: null,
    customPriceMin: null,
    customPriceMax: null,
    inStockOnly: false,
    minDiscount: null,
  });

  // Load category data and all products
  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const categories = await FetchNestedCategories();
        
        // Find the category by slug or ID
        const foundCategory = categories?.find(cat => 
          cat.slug === categorySlug || 
          cat.categoryId === categorySlug ||
          cat.name.toLowerCase().replace(/\s+/g, '-') === categorySlug
        );
        
        if (!foundCategory) {
          setError('Category not found');
          return;
        }
        
        setCategory(foundCategory);
        
        // Fetch ALL products from ALL subcategories at once (performance optimization)
        if (foundCategory.subCategories && foundCategory.subCategories.length > 0) {
          const productsPromises = foundCategory.subCategories.map(subCat => 
            FetchProductsBySubCategory(subCat.subCategoryId)
              .then(products => products || [])
              .catch(err => {
                console.error(`Failed to fetch products for ${subCat.name}:`, err);
                return [];
              })
          );
          
          const productsArrays = await Promise.all(productsPromises);
          const aggregatedProducts = productsArrays.flat();
          setAllProducts(aggregatedProducts);
        }
        
      } catch (error) {
        console.error('Error loading category:', error);
        setError('Failed to load category data');
      } finally {
        setLoading(false);
      }
    };

    if (categorySlug) {
      loadCategoryData();
    }
  }, [categorySlug]);

  // Client-side filtering - Apply all filters to products
  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    // Filter by subcategory
    if (filters.selectedSubCategories && filters.selectedSubCategories.length > 0) {
      filtered = filtered.filter(product => 
        filters.selectedSubCategories.includes(product.subCategoryId)
      );
    }

    // Filter by price range
    const minPrice = filters.customPriceMin || filters.priceMin;
    const maxPrice = filters.customPriceMax || filters.priceMax;
    
    if (minPrice !== null && minPrice !== undefined) {
      filtered = filtered.filter(product => {
        const discount = (product.discount / 100) || 0;
        const price = product.price * (1 - discount);
        return price >= minPrice;
      });
    }
    
    if (maxPrice !== null && maxPrice !== undefined && maxPrice !== Infinity) {
      filtered = filtered.filter(product => {
        const discount = (product.discount / 100) || 0;
        const price = product.price * (1 - discount);
        return price <= maxPrice;
      });
    }

    // Filter by availability
    if (filters.inStockOnly) {
      filtered = filtered.filter(product => product.stockQuantity > 0);
    }

    // Filter by minimum discount
    if (filters.minDiscount) {
      filtered = filtered.filter(product => {
        const discount = (product.discount / 100) || 0;
        return discount >= filters.minDiscount;
      });
    }

    return filtered;
  }, [allProducts, filters]);

  // Active filters count for mobile button
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.selectedSubCategories?.length > 0) count++;
    if (filters.priceMin || filters.priceMax || filters.customPriceMin || filters.customPriceMax) count++;
    if (filters.inStockOnly) count++;
    if (filters.minDiscount) count++;
    return count;
  }, [filters]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-orange-500 text-white px-6 py-2 rounded hover:bg-orange-600 mr-2"
          >
            Go Home
          </button>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Breadcrumb - Amazon style */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <nav className="flex text-sm text-gray-600">
            <Link to="/" className="hover:text-orange-600 hover:underline">Home</Link>
            <span className="mx-2">&rsaquo;</span>
            <span className="text-gray-900">{category?.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Container: 2-column layout (sidebar + content) */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          
          {/* LEFT COLUMN: Filters Sidebar - Desktop only, sticky */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FiltersSidebar
              filters={filters}
              onFilterChange={setFilters}
              subCategories={category?.subCategories || []}
            />
          </aside>

          {/* RIGHT COLUMN: Product Results */}
          <main className="flex-1 min-w-0">
            {/* Mobile: Filter Button + Results Count */}
            <div className="lg:hidden flex items-center justify-between mb-4 bg-white border border-gray-200 rounded-sm p-3">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{filteredProducts.length}</span> results
              </div>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-sm font-medium">Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            {/* Category Header */}
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-normal text-gray-900 mb-2">
                {category?.name}
              </h1>
              {category?.description && (
                <p className="text-sm text-gray-600">{category.description}</p>
              )}
            </div>

            {/* Product Grid with Pagination */}
            <ProductGrid 
              products={filteredProducts} 
              resultsText={category?.name}
            />
          </main>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <MobileFiltersDrawer
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        filters={filters}
        onFilterChange={setFilters}
        subCategories={category?.subCategories || []}
      />
    </div>
  );
};

export default CategoryPage;
